import { ReactNode, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closePopup, openPopup } from "../../App/Slices/popupSlice";
import { RootState } from "../../App/store";
import ContractService from "../../Services/ContractService";
import ToastService from "../../Services/ToastService";
import Spinner from "../shared/Spinner/Spinner";
import "./Deposit.scss";
import { Wallet, TestNetWallet } from "mainnet-js";
import ConfigService from "../../Services/ConfigService";
import { CashAddressNetworkPrefix, CashAddressType, encodeCashAddress, hexToBin } from "@bitauth/libauth";
import BchConnectorService from "../../Services/BchConnectorService";

export const Deposit = ({
  minPrice,
  children,
}: {
  minPrice: number;
  children?: ReactNode;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [depositAmount, setDepositAmount] = useState<number>(-1);
  let loadingToast: string;
  const disclaimerStorageKey = "acceptedDisclaimer";

  const userSelector = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();

  const deposit = async () => {
    return userSelector.user.coin === "BCH" ? depositBCH() : depositAVAX();
  };

  const depositAVAX = async () => {
    setLoading(true);

    try {
      await ContractService.setUpContract();

      const slimeShadiesSignerConnectedContract =
        await ContractService.getConnectedContract(
          ContractService.getSolidities().SlimeShadiesSolidity
        );

      slimeShadiesSignerConnectedContract
        .deposit({
          value: ContractService.parseEther(depositAmount.toString()),
        })
        .then((res: any) => {
          loadingToast = ToastService.loading(
            "Your request has been received. This process may take a few minutes."
          );
          closeModal();
          startDepositEventListener();
        })
        .catch((error: any) => {
          setLoading(false);
          ToastService.error(ContractService.handleErrorMessage(error));
        });
    } catch (error) {
      closeModal();
    }
  };

  const depositBCH = async () => {
    setLoading(true);

    try {
      const isDevelopmentMode = process.env.REACT_APP_WEBGL_RESOURCE_KEY !== "Prod";

      const depositPkhHex = await ConfigService.getBchDepositPkh();
      const depositAddress = encodeCashAddress(isDevelopmentMode ? CashAddressNetworkPrefix.testnet : CashAddressNetworkPrefix.mainnet, CashAddressType.p2pkh, hexToBin(depositPkhHex));
      const userWallet = await (isDevelopmentMode ? TestNetWallet : Wallet).watchOnly(userSelector.wallet!);
      const { unsignedTransaction, sourceOutputs } = await userWallet.send({
        cashaddr: depositAddress,
        value: depositAmount,
        unit: "bch"
      }, { buildUnsigned: true });

      const { signedTransaction } = await BchConnectorService.connector!.signTransaction({ transaction: unsignedTransaction!, sourceOutputs: sourceOutputs!, broadcast: false, userPrompt: "Sign to deposit BCH" });
      await userWallet.submitTransaction(hexToBin(signedTransaction), true)
        .then((res: any) => {
          ToastService.success(
            "Deposit transaction sent and will be credited in a few moments."
          );
          closeModal();
        })
        .catch((error: any) => {
          setLoading(false);
          ToastService.error(error.message ?? error);
        });
    } catch (error: any) {
      if ((error.message ?? error).includes("Amount required was not met")) {
        ToastService.error("Insufficient funds for deposit");
      } else {
        ToastService.error(error.message ?? error);
      }
      closeModal();
    }
  };

  const startDepositEventListener = () => {
    ContractService.getSolidities().SlimeShadiesSolidity.on(
      "Deposit",
      (player: string, amount: any) => {
        if (player.toLowerCase() == userSelector.user.wallet) {
          ToastService.success("Deposit successfully completed.", {
            id: loadingToast,
          });
          ContractService.getSolidities().SlimeShadiesSolidity.removeAllListeners(
            "Deposit"
          );
        }
      }
    );
  };

  const openDisclaimerModal = () => {
    dispatch(openPopup(disclaimer));
  };

  const closeDisclaimerModal = () => {
    localStorage.setItem(disclaimerStorageKey, "true");
    dispatch(closePopup(0));
    openModal();
  };

  const openModal = () => {
    if (!localStorage.getItem(disclaimerStorageKey)) {
      openDisclaimerModal();
      return;
    }
    dispatch(openPopup(depositModal));
  };

  const closeModal = () => {
    dispatch(closePopup(0));
    setTimeout(() => {
      setDepositAmount(-1);
      setLoading(false);
    }, 100);
  };

  const depositModal = useMemo(
    () => (
      <div className="deposit-modal">
        <div>
          <h2>Deposit {userSelector.user.coin} </h2>
          <div className="form-field">
            <label htmlFor="amount">Deposit amount</label>
            <input
              required
              placeholder="0"
              type="number"
              onChange={(e) => setDepositAmount(e.target.valueAsNumber)}
              id="amount"
            ></input>
            <span className="form-field-note">
              Minimum amount should be more than {minPrice}
            </span>
          </div>
        </div>
        <div className="actions">
          <div onClick={closeModal}>Cancel </div>
          <div
            className={
              loading || !depositAmount || depositAmount < (minPrice || 0)
                ? "disabled"
                : ""
            }
            onClick={() => {
              deposit();
            }}
          >
            {loading ? (
              <Spinner size={1.5} renderContent="Deposit"></Spinner>
            ) : (
              <>
                Deposit
                {depositAmount > 0 ? " " + depositAmount + ` ${userSelector.user.coin}` : ""}
              </>
            )}
          </div>
        </div>
      </div>
    ),
    [depositAmount, loading, userSelector]
  );

  const disclaimer = useMemo(
    () => (
      <div className="disclaimer-modal">
        <h3>Disclaimer</h3>
        <div>
          Please note that it is your responsibility to verify the legality of
          using Shady Battlegrounds in your jurisdiction before utilizing the
          platform. <br /> <br /> Shady Battlegrounds cannot be held accountable
          for any consequences or losses that may arise from your use of the
          platform. This includes financial losses, and we will not be liable
          for any such losses. <br /> <br />
          It is recommended that you exercise caution and conduct thorough
          research before using Shady Battlegrounds.
        </div>
        <button onClick={() => closeDisclaimerModal()}>Agree</button>
      </div>
    ),
    []
  );

  useEffect(() => {
    if (depositAmount == -1) return;
    openModal();
  }, [depositAmount, loading]);

  return <button onClick={openModal}>Deposit {children}</button>;
};
